import { useI18n } from '@/i18n/i18n-context'
import { Card } from '@/ui/card'
import { FaceGuideMask } from '@/ui/face-guide-mask'

/**
 * What is behind the browser's camera-permission dialog, for exactly as long as it is up.
 *
 * The dialog is raised by `getUserMedia` inside the patient's tap (scan-flow.tsx `onStart`), before
 * the scanning screen exists, so the start screen used to be what showed behind it. This is the one
 * line that belongs there instead. A browser that has remembered the answer resolves at once and this
 * screen is never seen.
 *
 * **IT IS THE SCANNING SCREEN'S SKELETON, EMPTY.** This used to be a card with a single line in it,
 * and the next thing the patient saw was a card three times as tall - so answering the dialog made
 * the page jump, which on a flow whose whole job is to look calm reads as a reload. Every box the
 * scanning screen occupies is occupied here at the same size: the line, the bar's track, and the
 * preview frame with the same guide drawn on it. Only their CONTENTS arrive later.
 *
 * The track is inert - no `role`, no `aria-valuenow`, no fill - because nothing has started and a
 * progress bar that announces itself to a screen reader while a permission dialog is up is claiming
 * a scan that does not exist. What it reserves is the height.
 */
export function CameraPermissionScreen() {
  const { t } = useI18n()
  return (
    <Card className="relative flex flex-col items-center gap-4">
      <p
        role="status"
        className="relative min-h-14 whitespace-pre-line text-center text-lg font-semibold text-slate-900"
      >
        {t('cameraPermission')}
      </p>

      <div aria-hidden="true" className="h-2 w-full overflow-hidden rounded-full bg-slate-200" />

      <div className="relative aspect-[315/382] w-full overflow-hidden rounded-xl bg-black">
        <FaceGuideMask className="pointer-events-none absolute inset-0 h-full w-full" />
      </div>
    </Card>
  )
}
